/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import React from 'react';
import { Box, Chip, Typography, Button, Collapse, IconButton, Stack } from '@mui/material';
import MaterialSymbol from '../../../components/MaterialSymbol';
import { colors } from '@farmlands/mui-theme';
import ProductCard from './ProductCard';
import OverdrawnBanner from './OverdrawnBanner';
import { DeliveryScheduleProduct } from './DeliverySchedule';

interface MonthCardProps {
  monthKey: string;
  products: DeliveryScheduleProduct[];
  lineDate: Date;
  isOpen: boolean;
  status: string;
  isCurrentMonth: boolean;
  isOverdrawn: boolean;
  isRequestable: boolean;
  toggleCollapse: () => void;
  onRequestDelivery: (line: Date) => void;
}

const ExpandLess = () => <MaterialSymbol>expand_less</MaterialSymbol>;
const ExpandMore = () => <MaterialSymbol>expand_more</MaterialSymbol>;

const MonthCard: React.FC<MonthCardProps> = ({
  monthKey,
  products,
  isOpen,
  status,
  isCurrentMonth,
  isOverdrawn,
  isRequestable,
  lineDate,
  toggleCollapse,
  onRequestDelivery,
}) => {
  return (
    <Stack display='flex' position='relative' width='100%'>
      {/* Dot */}
      <Box
        sx={{
          position: 'absolute',
          width: '20px',
          left: '-20px',
          top: '20px',
          mr: 2,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            width: '20px',
            height: '20px',
            backgroundColor: colors.primary200,
            borderRadius: '50%',
            border: `4px solid ${colors.primary50}`,
            zIndex: 1,
          }}
        />
      </Box>

      {/* Month Title and Chip */}
      <Stack onClick={toggleCollapse} sx={{ cursor: 'pointer' }} gap={0.5}>
        <Stack
          direction='row'
          display='flex'
          justifyContent='space-between'
          alignItems='center'
          mb={1}
        >
          <Typography variant='h4' m={1} mt={2.5}>
            {monthKey}
          </Typography>
          <Stack direction='row' alignItems='center'>
            <Chip
              label={status}
              sx={
                status === 'Open'
                  ? {
                      marginTop: '10px',
                      backgroundColor: colors.primary700,
                      color: colors.shade0,
                      textTransform: 'uppercase',
                      fontSize: '12px',
                      '&:hover': {
                        backgroundColor: colors.primary700,
                      },
                    }
                  : {
                      marginTop: '10px',
                      backgroundColor: colors.grey200,
                      color: colors.grey900,
                      textTransform: 'uppercase',
                      fontSize: '12px',
                      '&:hover': {
                        backgroundColor: colors.grey200,
                      },
                    }
              }
            />

            <IconButton>{isOpen ? <ExpandLess /> : <ExpandMore />}</IconButton>
          </Stack>
        </Stack>
      </Stack>
      {isRequestable ? (
        <Button
          variant={isCurrentMonth ? 'contained' : 'outlined'}
          color='primary'
          sx={{ my: 2 }}
          onClick={() => {
            onRequestDelivery(lineDate);
          }}
        >
          Create order
        </Button>
      ) : null}

      {/* Collapsible Product List */}
      <Collapse in={isOpen} timeout='auto' unmountOnExit>
        {products.map((product, index) => (
          <ProductCard key={`${product.productDescription}_${index}`} product={product} />
        ))}

        {isOverdrawn && <OverdrawnBanner />}
      </Collapse>
    </Stack>
  );
};

export default MonthCard;
